import React from 'react';

/**
 * Returns the page numbers (with '...' ellipsis) to display.
 * e.g. current=5, total=9 → [1, '...', 4, 5, 6, '...', 9]
 */
function getPageNumbers(current, total) {
    if (total <= 7) {
        return Array.from({ length: total }, (_, i) => i + 1);
    }

    const pages = [];
    const delta = 1; // neighbours around current page

    const left  = Math.max(2, current - delta);
    const right = Math.min(total - 1, current + delta);

    pages.push(1);

    if (left > 2) pages.push('...');

    for (let i = left; i <= right; i++) pages.push(i);

    if (right < total - 1) pages.push('...');

    pages.push(total);

    return pages;
}

/**
 * Shared pagination — renders with .ui-pagination class structure.
 *
 * Props:
 *   currentPage  {number}   – 1-based current page
 *   totalPages   {number}   – total page count
 *   onPageChange {function} – called with new page number (preferred)
 *   onPrev       {function} – fallback prev callback
 *   onNext       {function} – fallback next callback
 */
const TablePagination = ({ currentPage, totalPages, onPageChange, onPrev, onNext }) => {
    if (!totalPages || totalPages <= 1) return null;

    const goTo = (page) => {
        if (page < 1 || page > totalPages) return;
        if (onPageChange) {
            onPageChange(page);
        } else {
            if (page < currentPage && onPrev) onPrev();
            else if (page > currentPage && onNext) onNext();
        }
    };

    const pages = getPageNumbers(currentPage, totalPages);

    return (
        <div className="ui-pagination ui-pagination--center">
            <ul className="pagination">
                {/* Prev */}
                <li className={`page-item${currentPage === 1 ? ' disabled' : ''}`}>
                    <button
                        className="page-link"
                        onClick={() => goTo(currentPage - 1)}
                        disabled={currentPage === 1}
                        aria-label="Previous page"
                    >
                        «
                    </button>
                </li>

                {/* Numbered pages + ellipsis */}
                {pages.map((page, idx) =>
                    page === '...' ? (
                        <li key={`ellipsis-${idx}`} className="page-item disabled">
                            <span className="page-link ellipsis">…</span>
                        </li>
                    ) : (
                        <li
                            key={page}
                            className={`page-item${page === currentPage ? ' active' : ''}`}
                        >
                            <button
                                className="page-link"
                                onClick={() => goTo(page)}
                                aria-current={page === currentPage ? 'page' : undefined}
                            >
                                {page}
                            </button>
                        </li>
                    )
                )}

                {/* Next */}
                <li className={`page-item${currentPage === totalPages ? ' disabled' : ''}`}>
                    <button
                        className="page-link"
                        onClick={() => goTo(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        aria-label="Next page"
                    >
                        »
                    </button>
                </li>
            </ul>
        </div>
    );
};

export default TablePagination;
